/**
 * ویرایشِ رخدادی که **خودم** فرستادمش — قرینه‌ی حالتِ صاحب‌محورِ
 * apps/mobile/app/event/edit/[id].tsx.
 *
 * ⚠ عمداً فقط برای صاحبِ رخداد است، نه یک فرمِ ویرایشِ همگانی. روی موبایل هر
 * کاربرِ واردشده می‌تواند برای هر رخدادی «پیشنهادِ ویرایش» بدهد
 * (`propose_event_edit`, مایگریشن 0033) و آن فرم روی سایت هنوز نیست — یک
 * شکافِ قدیمیِ برابری که این صفحه پُرش نمی‌کند و ادعایش را هم ندارد. چیزی که
 * این‌جا لازم بود همان چیزی است که خواسته شده: کسی که رخدادش را اضافه کرده
 * بتواند خودش عوضش کند.
 *
 * دو رفتار، از روی همان چک‌باکسِ لحظه‌ی ساخت (مایگریشن 0077):
 *   برگزارکننده  →  تغییر همان لحظه لایو می‌شود.
 *   خبررسان      →  اگر رخداد منتشر شده باشد، تغییر می‌رود به صفِ بازبینی و
 *                   نسخه‌ی فعلی سرِ جایش می‌ماند.
 * کدامش اتفاق افتاده را خودِ `saveOwn` برمی‌گرداند، چون پیامِ بعدش فرق می‌کند.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eventRepo, tagRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { tagIdsOf, type EventEditDraft } from '@/core/domain/types';
import { Card, Help, Note, Select } from '@/ui/kit';
import { TagPicker } from '@/ui/TagPicker';
import { pickWideImage } from '@/features/profile/imagePicker';
import { DESCRIBE_KEYS } from '@/core/lib/myEvents';
import { usablePoint } from '@/core/lib/mapPicker';
import { LocationPicker } from './LocationPicker';

/** ISO → مقدارِ `<input type=date>`، به وقتِ محلی. */
const dateInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** ISO → مقدارِ `<input type=time>`، به وقتِ محلی. */
const timeInput = (iso: string | null | undefined, fallback: string): string => {
  if (!iso) return fallback;
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** روز + ساعت → یک لحظه‌ی واقعی. */
const instant = (day: string, clock: string): Date | undefined => {
  if (!day) return undefined;
  const [y, m, dd] = day.split('-').map(Number);
  const [h, mi] = clock.split(':').map(Number);
  return new Date(y, m - 1, dd, h || 0, mi || 0);
};

export function MyEventEditPage() {
  useLocale();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { profile } = useSession();

  const event = useQuery({
    queryKey: ['event', id],
    queryFn: () => eventRepo.byId(id!),
    enabled: !!id,
  });
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });

  const [d, setD] = useState<EventEditDraft | null>(null);
  const [day, setDay] = useState('');
  const [startTime, setStartTime] = useState('19:00');
  const [endTime, setEndTime] = useState('21:00');
  const [editNote, setEditNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  /** مرحله‌ی دومِ حذف: تا وقتی روشن نشده، دکمه‌ی حذف فقط سوال می‌پرسد. */
  const [asking, setAsking] = useState(false);
  /** حذف انجام شد — فرم دیگر معنایی ندارد. */
  const [gone, setGone] = useState(false);

  useEffect(() => {
    const e = event.data;
    if (!e || d) return;
    setD({
      kind: 'event',
      title: e.title, description: e.description ?? '', tagIds: tagIdsOf(e),
      isFree: e.isFree, priceNote: e.priceNote ?? '', url: e.url ?? '',
      ticketUrl: e.ticketUrl ?? '', imageUrl: e.imageUrl ?? '',
      // مکان روی خودِ ردیف است (مایگریشن 0079). `venueName` عمداً پر
      // نمی‌شود: رخدادِ کاربر ردیفِ venues ندارد.
      address: e.address ?? '', lat: e.lat ?? undefined, lng: e.lng ?? undefined,
    });
    setDay(dateInput(e.startsAt));
    setStartTime(timeInput(e.startsAt, '19:00'));
    setEndTime(timeInput(e.endsAt, '21:00'));
  }, [event.data, d]);

  const set = <K extends keyof EventEditDraft>(k: K, v: EventEditDraft[K]) =>
    setD((x) => (x ? { ...x, [k]: v } : x));

  const pickImage = async () => {
    setPicking(true);
    try {
      const picked = await pickWideImage();
      if (picked.kind !== 'ok') {
        if (picked.kind === 'error') setError(t('suggestImageFailed'));
        return;
      }
      set('imageUrl', await eventRepo.uploadImage(profile!.id, picked.base64));
      setError(null);
    } catch (e) {
      reportError('web.myEvent.image', e);
      setError(t('suggestImageFailed'));
    } finally {
      setPicking(false);
    }
  };

  const save = useMutation({
    mutationFn: () => eventRepo.saveOwn(event.data!.id, {
      ...d!,
      startsAt: instant(day, startTime),
      endsAt: instant(day, endTime),
    }, editNote),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['myEvents'] });
      qc.invalidateQueries({ queryKey: ['event', id] });
      qc.invalidateQueries({ queryKey: ['events'] });
      /**
       * صفحه عوض نمی‌شود و پیام همین‌جا می‌نشیند — برخلافِ موبایل که toast
       * دارد، وبِ این پروژه پیام را داخلِ خودِ صفحه نشان می‌دهد (نگاه کن به
       * features/profile/MyPhonesCard.tsx).
       *
       * و پیام باید بگوید واقعاً چه شد: «فرستاده شد برای بازبینی» برای چیزی
       * که همین الان لایو شده دروغ است — و برعکسش بدتر.
       */
      setSaved(res.needsReview ? t('myEventSavedForReview') : t('myEventSaved'));
      setError(null);
    },
    onError: (e) => { reportError('web.myEvent.save', e); setError(t('errorRetry')); },
  });

  /**
   * حذفِ رخدادِ خودم (مایگریشن 0078).
   *
   * تاییدِ دو مرحله‌ای داخلِ خودِ صفحه، نه `window.confirm`: بقیه‌ی سایت هم
   * همین‌طور است (نگاه کن به DeleteAccountPage) و پنجره‌ی مرورگر متنِ
   * چندخطیِ ما را با تایپوگرافیِ خودش نشان نمی‌دهد.
   */
  const remove = useMutation({
    mutationFn: async () => {
      await eventRepo.withdrawOwn(event.data!.id);
      await eventRepo.removeImage(event.data!.imageUrl).catch(() => undefined);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['myEvents'] });
      qc.invalidateQueries({ queryKey: ['events'] });
      setSaved(t('myEventDeleted'));
      setGone(true);
    },
    onError: (e) => { reportError('web.myEvent.withdraw', e); setError(t('errorRetry')); },
  });

  if (event.isLoading || (!d && event.data)) return <div className="feed"><p className="dim">…</p></div>;
  if (!event.data || !d) {
    return <div className="feed"><Card><p className="dim">{t('placeNotFound')}</p></Card></div>;
  }

  // حذف که انجام شد، فرمِ چیزی که دیگر وجود ندارد فقط گیج‌کننده است.
  if (gone) {
    return (
      <div className="feed">
        <Card>
          <p>{t('myEventDeleted')}</p>
          <Link className="btn" to="/discover">{t('discover')}</Link>
        </Card>
      </div>
    );
  }

  const mine = !!profile && event.data.submittedBy === profile.id;
  if (!mine) {
    return (
      <div className="feed">
        <Card>
          <p className="dim">{t('suggestReviewNote')}</p>
          <Link className="btn" to={`/event/${id}`}>{t('back')}</Link>
        </Card>
      </div>
    );
  }

  /**
   * پایانِ رخداد از همان یک روز و ساعتِ پایان ساخته می‌شود، پس «تاریخ لازم
   * است» یعنی همین یک فیلد. ساعتِ پایانِ کوچک‌تر از شروع (۲۲:۰۰ تا ۰۱:۰۰)
   * را سرور رد نمی‌کند ولی معنی هم نمی‌دهد، پس همین‌جا جلویش گرفته می‌شود.
   */
  const ends = instant(day, endTime);
  const starts = instant(day, startTime);
  const endOk = !!ends && !!starts && ends.getTime() > starts.getTime();
  const canSave = d.title.trim().length > 0 && d.url.trim().length > 0
    && endOk && !save.isPending;
  const willReview = event.data.status === 'published' && !event.data.isOrganizer;

  return (
    <div className="feed">
      <Link to={`/event/${id}`} className="dim">{t('back')}</Link>
      <h1 className="feed-title">{t('editEvent')}</h1>
      <p className="dim">{willReview ? t('editEventSub') : t('suggestOrganizerHint')}</p>

      <Card>
        <label className="field">
          <span>{t('suggestNameEvent')}</span>
          <input className="input" value={d.title}
            onChange={(e) => set('title', e.target.value)} />
        </label>

        {/* عکس بلافاصله بعد از اسم، نه ته فرم — همان جابه‌جاییِ فرمِ پیشنهاد:
            کسی که برمی‌گردد تا عکس اضافه کند نباید دنبالش بگردد.
            «چرا عکس» جلوی چشم، نه پشتِ «؟» — متنِ ترغیبی است. */}
        <h3>{t('suggestImage')}</h3>
        <p className="dim">{t('suggestImageWhy')}</p>
        {d.imageUrl ? <img className="hero-img" src={d.imageUrl} alt="" /> : null}
        <div className="chiprow">
          <button type="button" className="btn" disabled={picking} onClick={pickImage}>
            {d.imageUrl ? t('suggestImageChange') : t('suggestImagePick')}
          </button>
          {d.imageUrl ? (
            <button type="button" className="btn" onClick={() => set('imageUrl', '')}>
              {t('suggestImageRemove')}
            </button>
          ) : null}
        </div>

        <label className="field">
          {/* راهنمای «چه بنویسم» پشتِ همان «؟»، نه هفت خطِ زیرِ کادر. */}
          <span>{t('suggestDescription')}<Help lines={DESCRIBE_KEYS.map((k) => t(k))} /></span>
          <textarea className="input textarea" rows={4} value={d.description}
            onChange={(e) => set('description', e.target.value)} />
        </label>

        <h3>{t('category')}</h3>
        <TagPicker value={d.tagIds} onChange={(ids) => set('tagIds', ids)}
          tags={tags.data ?? []} />

        {/* مکانِ رخدادِ خودت روی خودِ ردیف می‌نشیند (مایگریشن 0079) و روی
            نقشه هم قابلِ انتخاب است. */}
        <LocationPicker
          address={d.address ?? ''} onAddress={(v) => set('address', v)}
          point={usablePoint(d) ? { lat: d.lat!, lng: d.lng! } : null}
          onPoint={(pt) => setD((x) => (x ? { ...x, lat: pt?.lat, lng: pt?.lng } : x))} />

        <div className="two-up">
          <label className="field">
            <span>{t('pickDate')}<Help text={t('suggestEndRequired')} /></span>
            <input className="input" type="date" value={day} required
              onChange={(e) => setDay(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('when')}</span>
            <div className="two-up">
              <input className="input" type="time" value={startTime}
                onChange={(e) => setStartTime(e.target.value)} />
              <input className="input" type="time" value={endTime}
                onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </label>
        </div>
        {/* دوحالته هم دراپ‌دان — همون الگوی فرمِ پیشنهاد. */}
        <Select value={d.isFree ? 'free' : 'paid'}
          items={[
            { value: 'paid', label: t('notFree') },
            { value: 'free', label: t('onlyFree') },
          ]}
          onChange={(v) => set('isFree', v === 'free')} />
        {!d.isFree ? (
          <label className="field">
            <span>{t('priceNoteLabel')}</span>
            <input className="input" value={d.priceNote}
              onChange={(e) => set('priceNote', e.target.value)} placeholder={t('priceNotePh')} />
          </label>
        ) : null}

        <label className="field">
          <span>{t('suggestUrl')}<Help text={t('suggestUrlWhy')} /></span>
          <input className="input" value={d.url}
            onChange={(e) => set('url', e.target.value)} placeholder="example.com" />
        </label>
        <label className="field">
          <span>{t('suggestTicketUrl')}<Help text={t('suggestTicketUrlHint')} /></span>
          <input className="input" value={d.ticketUrl}
            onChange={(e) => set('ticketUrl', e.target.value)}
            placeholder="example.com/tickets" />
        </label>

        <label className="field">
          <span>{t('editEventNote')}</span>
          <input className="input" value={editNote}
            onChange={(e) => setEditNote(e.target.value)} placeholder={t('editEventNotePh')} />
        </label>

        {error ? <p className="error">{error}</p> : null}
        {saved ? <Note>{saved}</Note> : null}
        <button type="button" className="btn fill block" disabled={!canSave}
          onClick={() => save.mutate()}>
          {save.isPending ? t('saving') : (willReview ? t('editEventSend') : t('myEventEdit'))}
        </button>
      </Card>

      {willReview ? <Note>{t('suggestReviewNote')}</Note> : null}

      {/* حذف در کارتِ خودش و ته صفحه: کاری که برگشت ندارد نباید کنارِ دکمه‌ی
          ذخیره بنشیند. یک کلیک فقط سوال می‌پرسد؛ کلیکِ دوم انجامش می‌دهد. */}
      <Card>
        {asking ? (
          <>
            <p>{t('myEventDeleteConfirm')}</p>
            <div className="chiprow">
              <button type="button" className="btn" disabled={remove.isPending}
                onClick={() => remove.mutate()}>
                {remove.isPending ? t('saving') : t('remove')}
              </button>
              <button type="button" className="btn" onClick={() => setAsking(false)}>
                {t('contactsPrivacyCancel')}
              </button>
            </div>
          </>
        ) : (
          <button type="button" className="btn" onClick={() => setAsking(true)}>
            {t('myEventDelete')}
          </button>
        )}
      </Card>
    </div>
  );
}
